import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Title, Meta } from "react-head";
import { motion } from "motion/react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiDownload, FiVideo, FiImage, FiCheck, FiUpload, FiTrash2, FiCircle, FiSquare, FiMaximize2, FiRotateCw, FiRefreshCw, FiLayers, FiType, FiBold, FiItalic, FiPlus, FiChevronDown, FiX } from "react-icons/fi";
import { MdRoundedCorner } from "react-icons/md";

import ClientCalling from "../api/ClientCalling";
import clientToast from "../components/Client/clientToast";
import SmartImage from "../components/Client/SmartImage";
import InpixFooter from "../components/Client/InpixFooter";
import ClientAdSense from "../components/Client/ClientAdSense";
import { downloadFile, downloadComposedImage, downloadComposedVideo } from "../components/inpixCompose";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const SHAPES = [
    { key: "circle", label: "Circle", icon: <FiCircle />, radius: 0.5 },
    { key: "rounded", label: "Rounded", icon: <MdRoundedCorner />, radius: 0.18 },
    { key: "square", label: "Square", icon: <FiSquare />, radius: 0 },
];
const radiusCss = (radius) => `${Math.max(0, Math.min(radius ?? 0.5, 0.5)) * 100}%`;
// luminance check so the highlight pill contrasts the text color
const isLightColor = (hex) => {
    try {
        const c = (hex || "#ffffff").replace("#", "");
        const n = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
        const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
        return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
    } catch (e) { return true; }
};
const highlightBg = (color) => (isLightColor(color) ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.9)");
const FONTS = [
    { label: "Sans", css: "Arial, sans-serif" },
    { label: "Serif", css: "Georgia, serif" },
    { label: "Mono", css: '"Courier New", monospace' },
    { label: "Impact", css: "Impact, Haettenschweiler, sans-serif" },
    { label: "Script", css: '"Comic Sans MS", cursive' },
    { label: "Times", css: '"Times New Roman", serif' },
    { label: "Verdana", css: "Verdana, sans-serif" },
    { label: "Trebuchet", css: '"Trebuchet MS", sans-serif' },
    { label: "Palatino", css: '"Palatino Linotype", "Book Antiqua", serif' },
    { label: "Tahoma", css: "Tahoma, sans-serif" },
    { label: "Garamond", css: "Garamond, serif" },
    { label: "Brush", css: '"Brush Script MT", cursive' },
];
const COLORS = ["#ffffff", "#000000", "#FCFA02", "#FF8AFF", "#7F00FF", "#22c55e", "#ef4444", "#38bdf8"];
const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
const PHOTO_MAX_MB = 15;

// Checkerboard so transparent frame thumbnails read clearly
const CHECKER = {
    backgroundImage:
        "linear-gradient(45deg,#9993 25%,transparent 25%),linear-gradient(-45deg,#9993 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#9993 75%),linear-gradient(-45deg,transparent 75%,#9993 75%)",
    backgroundSize: "12px 12px",
    backgroundPosition: "0 0,0 6px,6px -6px,-6px 0",
};

const REL_LIMIT = 12;

/* ---------- Loading shimmer ---------- */
const ViewSkeleton = () => (
    <div className="min-h-screen dm-sans client-home-bg pb-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 pt-4">
            {/* header */}
            <div className="flex items-center gap-3 py-2 mb-4">
                <div className="w-9 h-9 rounded-full ios-shimmer" />
                <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 w-1/2 rounded-full ios-shimmer" />
                    <div className="h-2.5 w-1/4 rounded-full ios-shimmer" />
                </div>
                <div className="w-14 h-6 rounded-full ios-shimmer" />
            </div>
            {/* stage */}
            <div className="mx-auto rounded-3xl ios-shimmer" style={{ aspectRatio: "9 / 16", maxHeight: "62vh", width: "auto", maxWidth: "100%" }} />
            {/* frame strip */}
            <div className="flex gap-2.5 mt-5">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="w-14 rounded-xl ios-shimmer" style={{ aspectRatio: "9 / 16" }} />
                ))}
            </div>
            {/* button */}
            <div className="h-12 rounded-full ios-shimmer mt-5" />
        </div>
    </div>
);

/* ---------- Related card ---------- */
const RelatedCard = ({ item, onOpen }) => {
    const isVideo = item.i_type === "video";
    // Video loads ONLY on hover → no bandwidth until previewed.
    const [hover, setHover] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onOpen(item)}
            onMouseEnter={() => isVideo && setHover(true)}
            onMouseLeave={() => isVideo && setHover(false)}
            className="relative aspect-[9/16] rounded-2xl cursor-pointer group bg-client-back"
            style={{ overflow: "clip", boxShadow: "0 10px 26px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)" }}
        >
            <SmartImage src={item.i_thumbnail || item.i_media} alt={item.i_name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            {isVideo && hover && (
                <video src={item.i_media} muted loop autoPlay playsInline preload="auto"
                    className="absolute inset-0 w-full h-full object-cover" />
            )}
            <div className="absolute inset-x-0 bottom-0 h-3/5 pointer-events-none"
                style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)" }} />
            {isVideo && (
                <span className="absolute top-2 left-2 ios-glass-dark px-1.5 py-0.5 rounded-full flex items-center text-white">
                    <FiVideo className="text-[10px]" />
                </span>
            )}
            <p className="absolute inset-x-0 bottom-0 p-2 text-white text-[10px] md:text-xs font-semibold truncate"
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}>
                {item.i_name}
            </p>
        </motion.div>
    );
};

const ClientInpixView = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [overlayId, setOverlayId] = useState(null); // selected frame
    const [busy, setBusy] = useState(false);

    const [related, setRelated] = useState([]);

    // collapsible editor sections
    const [showPhoto, setShowPhoto] = useState(true);
    const [showText, setShowText] = useState(true);

    // ---- User photo overlay (Canva-like: drag / resize / shape) ----
    const [userPhoto, setUserPhoto] = useState(null); // { url, file }
    const [photo, setPhoto] = useState({ x: 0.5, y: 0.58, w: 0.34, h: 0.19, radius: 0.5, rotation: 0, order: "back", borderColor: "#ffffff", borderWidth: 0 });
    const stageRef = useRef(null);
    const dragRef = useRef(null);

    const onPointerMove = useCallback((e) => {
        const d = dragRef.current;
        if (!d || !stageRef.current) return;
        const rect = stageRef.current.getBoundingClientRect();
        const dx = (e.clientX - d.startX) / rect.width;
        const dy = (e.clientY - d.startY) / rect.height;
        if (d.mode === "move") {
            setPhoto((p) => ({ ...p, x: clamp(d.sx + dx, 0, 1), y: clamp(d.sy + dy, 0, 1) }));
        } else if (d.mode === "resize") {
            setPhoto((p) => ({ ...p, w: clamp(d.sw + dx * 2, 0.06, 1.4), h: clamp(d.sh + dy * 2, 0.04, 1.4) }));
        } else if (d.mode === "rotate") {
            let ang = Math.round(Math.atan2(e.clientY - d.cy, e.clientX - d.cx) * 180 / Math.PI + 90);
            if (ang > 180) ang -= 360; if (ang < -180) ang += 360;
            setPhoto((p) => ({ ...p, rotation: ang }));
        } else if (d.mode === "text-move") {
            setTexts((ts) => ts.map((t) => (t.id === d.id ? { ...t, x: clamp(d.sx + dx, 0, 1), y: clamp(d.sy + dy, 0, 1) } : t)));
        } else if (d.mode === "text-resize") {
            setTexts((ts) => ts.map((t) => (t.id === d.id ? { ...t, size: clamp(d.ssize + dx * 0.6, 0.02, 0.4) } : t)));
        }
    }, []);

    const onPointerUp = useCallback(() => {
        dragRef.current = null;
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
    }, [onPointerMove]);

    const startMove = (e) => {
        e.stopPropagation(); e.preventDefault();
        dragRef.current = { mode: "move", startX: e.clientX, startY: e.clientY, sx: photo.x, sy: photo.y };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
    };
    const startResize = (e) => {
        e.stopPropagation(); e.preventDefault();
        dragRef.current = { mode: "resize", startX: e.clientX, startY: e.clientY, sw: photo.w, sh: photo.h };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
    };
    const startRotate = (e) => {
        e.stopPropagation(); e.preventDefault();
        const rect = stageRef.current.getBoundingClientRect();
        dragRef.current = { mode: "rotate", cx: rect.left + photo.x * rect.width, cy: rect.top + photo.y * rect.height };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
    };

    const photoInputRef = useRef(null);
    const [photoDrag, setPhotoDrag] = useState(false);

    // Validate (type + size) and accept a user photo from click / drag-drop / paste.
    const acceptPhotoFile = useCallback((f) => {
        if (!f) return;
        if (!f.type || !f.type.startsWith("image/")) { clientToast.error("Please choose an image file."); return; }
        if (!PHOTO_TYPES.includes(f.type)) { clientToast.error("Unsupported type — use JPG, PNG, WebP or GIF."); return; }
        if (f.size > PHOTO_MAX_MB * 1024 * 1024) { clientToast.error(`Image too large (max ${PHOTO_MAX_MB}MB).`); return; }
        setUserPhoto((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return { url: URL.createObjectURL(f), file: f }; });
        setPhoto((p) => ({ ...p, x: 0.5, y: 0.58 }));
    }, []);

    const onPickPhoto = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = "";
        acceptPhotoFile(f);
    };
    const onPhotoDrop = (e) => {
        e.preventDefault(); setPhotoDrag(false);
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) acceptPhotoFile(f);
    };

    // Paste an image anywhere on the page → use it as the photo
    useEffect(() => {
        const onPaste = (e) => {
            const items = (e.clipboardData && e.clipboardData.items) || [];
            for (const it of items) {
                if (it.kind === "file") { const f = it.getAsFile(); if (f) { acceptPhotoFile(f); break; } }
            }
        };
        window.addEventListener("paste", onPaste);
        return () => window.removeEventListener("paste", onPaste);
    }, [acceptPhotoFile]);
    const removePhoto = () => setUserPhoto((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return null; });
    const resetPhoto = () => setPhoto((p) => ({ ...p, x: 0.5, y: 0.58, w: 0.34, h: 0.19, rotation: 0 }));
    const toggleOrder = () => setPhoto((p) => ({ ...p, order: p.order === "front" ? "back" : "front" }));

    // revoke object URL on unmount
    useEffect(() => () => { if (userPhoto?.url) URL.revokeObjectURL(userPhoto.url); }, [userPhoto]);

    // fully lock background scroll while the processing dialog is open
    useEffect(() => {
        if (!busy) return;
        const prevBody = document.body.style.overflow;
        const prevHtml = document.documentElement.style.overflow;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        const prevent = (e) => e.preventDefault();
        window.addEventListener("wheel", prevent, { passive: false });
        window.addEventListener("touchmove", prevent, { passive: false });
        return () => {
            document.body.style.overflow = prevBody;
            document.documentElement.style.overflow = prevHtml;
            window.removeEventListener("wheel", prevent);
            window.removeEventListener("touchmove", prevent);
        };
    }, [busy]);

    // ---- Text layers (Canva-like: add / style / drag) ----
    const [texts, setTexts] = useState([]);
    const [selTextId, setSelTextId] = useState(null);
    const idRef = useRef(1);
    const [stageW, setStageW] = useState(0);

    // track on-screen stage width so text font-size (a fraction of width) renders correctly
    useEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setStageW(el.clientWidth));
        ro.observe(el);
        setStageW(el.clientWidth);
        return () => ro.disconnect();
    }, [data]);

    const selText = texts.find((t) => t.id === selTextId) || null;
    const addText = () => {
        const id = idRef.current++;
        setTexts((ts) => [...ts, { id, content: "Your text", x: 0.5, y: 0.4, size: 0.07, color: "#ffffff", font: FONTS[0].css, bold: true, italic: false, stroke: false, bg: false, rotation: 0 }]);
        setSelTextId(id);
    };
    const updateText = (id, patch) => setTexts((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    const updateSel = (patch) => { if (selTextId != null) updateText(selTextId, patch); };
    const removeText = (id) => { setTexts((ts) => ts.filter((t) => t.id !== id)); setSelTextId((s) => (s === id ? null : s)); };

    const startTextMove = (id, e) => {
        e.stopPropagation(); e.preventDefault();
        setSelTextId(id);
        const t = texts.find((x) => x.id === id);
        dragRef.current = { mode: "text-move", id, startX: e.clientX, startY: e.clientY, sx: t.x, sy: t.y };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
    };
    const startTextResize = (id, e) => {
        e.stopPropagation(); e.preventDefault();
        const t = texts.find((x) => x.id === id);
        dragRef.current = { mode: "text-resize", id, startX: e.clientX, startY: e.clientY, ssize: t.size };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
    };

    // Fetch the template
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true); setNotFound(false); setRelated([]);
            setUserPhoto(null); setPhoto({ x: 0.5, y: 0.58, w: 0.34, h: 0.19, radius: 0.5, rotation: 0, order: "back", borderColor: "#ffffff", borderWidth: 0 });
            setTexts([]); setSelTextId(null);
            try {
                const resp = await ClientCalling.getInpixBySlugClient(slug);
                if (!alive) return;
                if (resp?.status && resp.data) { setData(resp.data); setOverlayId(null); }
                else setNotFound(true);
            } catch (e) {
                if (alive) setNotFound(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [slug]);

    // Fetch related templates from the same category (excluding the current one)
    useEffect(() => {
        const catSlug = data?.i_category?.i_slug;
        if (!catSlug) return;
        let alive = true;
        (async () => {
            try {
                const resp = await ClientCalling.getInpixByCategoryClient(catSlug, 1, REL_LIMIT + 1, { sortBy: "createdAt", order: "desc" });
                if (!alive) return;
                if (resp?.status && Array.isArray(resp.data)) {
                    setRelated(resp.data.filter((t) => t.i_slug !== data.i_slug).slice(0, REL_LIMIT));
                }
            } catch (e) { /* ignore */ }
        })();
        return () => { alive = false; };
    }, [data?.i_category?.i_slug, data?.i_slug]);

    const connected = (data?.i_frames || []).filter((f) => f && f._id);
    const overlay = connected.find((f) => f._id === overlayId);
    const isVideo = data?.i_type === "video";

    const handleDownload = useCallback(async () => {
        if (!data) return;
        setBusy(true);
        try {
            const name = data.i_slug || "status";
            const photoSpec = userPhoto ? { url: userPhoto.url, ...photo } : null;
            const frameUrl = overlay ? overlay.f_image : null;
            const textLayers = texts.filter((t) => (t.content || "").trim());
            const plain = !photoSpec && !frameUrl && textLayers.length === 0; // nothing to composite → original

            if (plain) {
                await downloadFile(data.i_media, `${name}.${isVideo ? "mp4" : "jpg"}`);
            } else if (!isVideo) {
                try {
                    await downloadComposedImage({ baseUrl: data.i_media, frameUrl, photo: photoSpec, texts: textLayers, filename: `${name}-custom.jpg` });
                } catch (e) {
                    clientToast.info("Couldn't merge in-browser — downloading the original.");
                    await downloadFile(data.i_media, `${name}.jpg`);
                }
            } else {
                try {
                    clientToast.info("Merging your status… please wait.");
                    await downloadComposedVideo({ videoUrl: data.i_media, frameUrl, photo: photoSpec, texts: textLayers, filename: `${name}-custom.webm` });
                } catch (e) {
                    clientToast.info("Couldn't merge in-browser — downloading the original.");
                    await downloadFile(data.i_media, `${name}.mp4`);
                }
            }
            ClientCalling.recordInpixDownloadClient(name);
            clientToast.success("Download started!");
        } finally {
            setBusy(false);
        }
    }, [data, overlay, isVideo, userPhoto, photo, texts]);

    const openTemplate = (item) => navigate(`/inpix/view/${item.i_slug}`);

    if (loading) return <ViewSkeleton />;

    /* ---------- Not found ---------- */
    if (notFound || !data) {
        return (
            <div className="min-h-screen dm-sans client-home-bg flex flex-col items-center justify-center text-center px-6">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
                    style={{ background: "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))", border: "1px solid rgba(255,255,255,0.12)" }}>
                    <FiImage className="text-2xl text-client-title" />
                </div>
                <h3 className="text-client-font font-bold text-xl">Status not found</h3>
                <p className="text-client-font/55 text-sm mt-2">It may have been removed or the link is wrong.</p>
                <button onClick={() => navigate("/inpix")}
                    className="mt-6 px-5 py-2.5 rounded-full text-white text-sm font-semibold"
                    style={{ background: "linear-gradient(135deg, #7F00FF, #FF8AFF)" }}>
                    Browse status
                </button>
            </div>
        );
    }

    const pageTitle = `${data.i_name} | Status | PicPromptify`;
    const pageDesc = `Download "${data.i_name}"${data.i_category?.i_name ? ` from ${data.i_category.i_name}` : ""} status on PicPromptify — add a frame and share.`;
    const ogImage = data.i_thumbnail || data.i_media;

    return (
        <div className="min-h-screen dm-sans client-home-bg">
            {/* ---- SEO ---- */}
            <Title>{pageTitle}</Title>
            <Meta name="description" content={pageDesc} />
            <Meta name="keywords" content={`${data.i_name}, ${data.i_category?.i_name || "status"}, status maker, ${data.i_type} status, poster, quotes, PicPromptify`} />
            <Meta property="og:title" content={pageTitle} />
            <Meta property="og:description" content={pageDesc} />
            <Meta property="og:type" content="article" />
            <Meta property="og:url" content={`https://picpromptify.com/inpix/view/${data.i_slug}`} />
            {ogImage && <Meta property="og:image" content={ogImage} />}
            <Meta name="twitter:card" content="summary_large_image" />
            {ogImage && <Meta name="twitter:image" content={ogImage} />}

            {/* ---- Header ---- */}
            <div className="sticky top-0 z-40 wp-catbar">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center gap-3 py-3">
                    <button onClick={() => navigate(-1)}
                        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-client-font/85"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} aria-label="Back">
                        <FiArrowLeft className="text-base" />
                    </button>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-client-font font-semibold text-sm md:text-base truncate">{data.i_name}</h1>
                        {data.i_category?.i_name && (
                            <button onClick={() => navigate(`/inpix/category/${data.i_category.i_slug}`)}
                                className="text-client-primary/90 hover:text-client-title text-[11px] truncate">
                                {data.i_category.i_name}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <section className="mx-auto max-w-3xl px-4 sm:px-6 pt-5">
                {/* top banner ad */}
                <ClientAdSense slot='9729451986' containerClassName='w-full mb-5' />

                {/* ---- Composite stage ---- */}
                <motion.div ref={stageRef} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    className="relative mx-auto rounded-3xl overflow-hidden"
                    style={{ aspectRatio: "9 / 16", maxHeight: "66vh", width: "auto", maxWidth: "100%", boxShadow: "0 24px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)" }}>
                    {isVideo
                        ? <video src={data.i_media} poster={data.i_thumbnail} muted loop autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />
                        : <img src={data.i_media} alt={data.i_name} className="absolute inset-0 w-full h-full object-cover" />}

                    {/* user photo — drag to move, corner to resize, rotate/order via toolbar */}
                    {userPhoto && (
                        <div onPointerDown={startMove}
                            className="absolute select-none group/photo"
                            style={{
                                left: `${photo.x * 100}%`, top: `${photo.y * 100}%`,
                                width: `${photo.w * 100}%`, height: `${photo.h * 100}%`,
                                transform: `translate(-50%,-50%) rotate(${photo.rotation || 0}deg)`,
                                touchAction: "none", cursor: "grab",
                                zIndex: photo.order === "front" ? 30 : 10,
                            }}>
                            <img src={userPhoto.url} alt="you" draggable={false}
                                style={{
                                    width: "100%", height: "100%", objectFit: "cover", boxSizing: "border-box",
                                    borderRadius: radiusCss(photo.radius),
                                    border: photo.borderWidth > 0 && photo.borderColor
                                        ? `${Math.min(photo.w * stageW, photo.h * stageW * 16 / 9) * photo.borderWidth || 2}px solid ${photo.borderColor}`
                                        : "none",
                                    boxShadow: "0 2px 12px rgba(0,0,0,0.45)",
                                }} />
                            {/* selection outline */}
                            <div className="absolute inset-0 pointer-events-none"
                                style={{ boxShadow: "0 0 0 1.5px rgba(127,0,255,0.9)", borderRadius: radiusCss(photo.radius) }} />
                            {/* delete (top-left) */}
                            <button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); removePhoto(); }}
                                className="absolute flex items-center justify-center"
                                style={{ left: "-9px", top: "-9px", width: "20px", height: "20px", borderRadius: "9999px", background: "#ef4444", border: "2px solid #fff", cursor: "pointer", touchAction: "none" }} aria-label="Remove photo">
                                <FiX style={{ fontSize: 11, color: "#fff" }} />
                            </button>
                            {/* rotate handle (top) */}
                            <div onPointerDown={startRotate} className="absolute flex items-center justify-center"
                                style={{ left: "50%", top: "-30px", transform: "translateX(-50%)", width: "20px", height: "20px", borderRadius: "9999px", background: "#fff", border: "2px solid #7F00FF", cursor: "grab", touchAction: "none" }}>
                                <FiRotateCw style={{ fontSize: 9, color: "#7F00FF" }} />
                            </div>
                            {/* resize handle (corner) */}
                            <div onPointerDown={startResize}
                                className="absolute"
                                style={{
                                    right: "-9px", bottom: "-9px", width: "20px", height: "20px", borderRadius: "9999px",
                                    background: "#7F00FF", border: "2px solid #fff", cursor: "nwse-resize", touchAction: "none",
                                }} />
                        </div>
                    )}

                    {overlay && (
                        <img src={overlay.f_image || overlay.f_thumbnail} alt={overlay.f_name}
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ zIndex: 20 }} />
                    )}

                    {/* text layers — drag to move, corner to resize (top-most) */}
                    {texts.map((t) => (
                        <div key={t.id} onPointerDown={(e) => startTextMove(t.id, e)}
                            className="absolute select-none"
                            style={{
                                left: `${t.x * 100}%`, top: `${t.y * 100}%`,
                                transform: `translate(-50%,-50%) rotate(${t.rotation || 0}deg)`,
                                zIndex: 40, touchAction: "none", cursor: "grab", maxWidth: "94%",
                            }}>
                            <span style={{
                                display: "inline-block", whiteSpace: "pre", lineHeight: 1.15,
                                fontFamily: t.font, fontWeight: t.bold ? 700 : 400, fontStyle: t.italic ? "italic" : "normal", color: t.color,
                                fontSize: stageW ? `${t.size * stageW}px` : "24px",
                                textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                WebkitTextStroke: t.stroke ? `${(stageW ? t.size * stageW : 24) * 0.07}px #000` : undefined,
                                background: t.bg ? highlightBg(t.color) : "transparent",
                                padding: t.bg ? "0.12em 0.36em" : 0,
                                borderRadius: t.bg ? "0.22em" : 0,
                                outline: selTextId === t.id ? "1.5px dashed rgba(127,0,255,0.9)" : "none", outlineOffset: "4px",
                            }}>
                                {t.content || " "}
                            </span>
                            {selTextId === t.id && (
                                <>
                                    <button onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); removeText(t.id); }}
                                        className="absolute flex items-center justify-center"
                                        style={{ left: "-12px", top: "-12px", width: "18px", height: "18px", borderRadius: "9999px", background: "#ef4444", border: "2px solid #fff", cursor: "pointer", touchAction: "none" }} aria-label="Remove text">
                                        <FiX style={{ fontSize: 10, color: "#fff" }} />
                                    </button>
                                    <div onPointerDown={(e) => startTextResize(t.id, e)}
                                        className="absolute"
                                        style={{ right: "-12px", bottom: "-12px", width: "18px", height: "18px", borderRadius: "9999px", background: "#7F00FF", border: "2px solid #fff", cursor: "nwse-resize", touchAction: "none" }} />
                                </>
                            )}
                        </div>
                    ))}
                    <span className="absolute top-2.5 left-2.5 ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[10px] font-medium">
                        {isVideo ? <FiVideo className="text-xs" /> : <FiImage className="text-xs" />}
                        {isVideo ? "Video" : "Image"}
                    </span>
                </motion.div>

                {/* ---- Your photo ---- */}
                <div className="mt-5">
                    <button onClick={() => setShowPhoto((s) => !s)} className="flex items-center justify-between w-full mb-2">
                        <span className="text-client-font/70 text-xs font-semibold uppercase tracking-wider">Your photo</span>
                        <FiChevronDown className="text-client-font/55 transition-transform" style={{ transform: showPhoto ? "rotate(180deg)" : "none" }} />
                    </button>
                    {showPhoto && (!userPhoto ? (
                        <div role="button" tabIndex={0}
                            onClick={() => photoInputRef.current && photoInputRef.current.click()}
                            onDragOver={(e) => { e.preventDefault(); setPhotoDrag(true); }}
                            onDragLeave={() => setPhotoDrag(false)}
                            onDrop={onPhotoDrop}
                            className="flex flex-col items-center justify-center gap-1.5 w-full py-6 rounded-xl border-2 border-dashed cursor-pointer text-center transition"
                            style={{ borderColor: photoDrag ? "#B14BFF" : "rgba(255,255,255,0.18)", background: photoDrag ? "rgba(127,0,255,0.08)" : "rgba(255,255,255,0.03)" }}>
                            <FiUpload className="text-xl text-client-font/60" />
                            <p className="text-client-font/75 text-sm font-medium">Drag &amp; drop, click, or paste your photo</p>
                            <p className="text-client-font/40 text-[11px]">JPG · PNG · WebP · GIF — up to {PHOTO_MAX_MB}MB</p>
                            <input ref={photoInputRef} type="file" accept={PHOTO_TYPES.join(",")} className="hidden" onChange={onPickPhoto} />
                        </div>
                    ) : (
                        <div className="rounded-2xl p-3.5 space-y-3.5"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            {/* Shape + corner radius */}
                            <div>
                                <p className="text-client-font/55 text-[10px] uppercase tracking-wider mb-1.5">Shape</p>
                                <div className="flex items-center gap-2">
                                    {SHAPES.map((s) => (
                                        <button key={s.key} onClick={() => setPhoto((p) => ({ ...p, radius: s.radius }))}
                                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition"
                                            style={Math.abs((photo.radius ?? 0.5) - s.radius) < 0.001
                                                ? { color: "#fff", background: "linear-gradient(135deg,#7F00FF,#FF8AFF)", boxShadow: "0 6px 16px -6px rgba(127,0,255,0.6)" }
                                                : { color: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                            {s.icon}{s.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-client-font/55 text-[10px] uppercase tracking-wider">Corner radius</span>
                                        <span className="text-client-font/70 text-[10px] tabular-nums">{Math.round((photo.radius ?? 0.5) * 100)}</span>
                                    </div>
                                    <input type="range" min="0" max="50" value={Math.round((photo.radius ?? 0.5) * 100)}
                                        onChange={(e) => setPhoto((p) => ({ ...p, radius: Number(e.target.value) / 100 }))}
                                        className="w-full" style={{ accentColor: "#7F00FF" }} />
                                </div>
                            </div>

                            {/* Border */}
                            <div>
                                <p className="text-client-font/55 text-[10px] uppercase tracking-wider mb-1.5">Border</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button onClick={() => setPhoto((p) => ({ ...p, borderWidth: 0 }))}
                                        className="px-3 py-1.5 rounded-lg text-xs font-medium"
                                        style={photo.borderWidth === 0
                                            ? { color: "#fff", background: "linear-gradient(135deg,#7F00FF,#FF8AFF)" }
                                            : { color: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                        None
                                    </button>
                                    {COLORS.map((c) => {
                                        const on = photo.borderWidth > 0 && photo.borderColor === c;
                                        return (
                                            <button key={c} onClick={() => setPhoto((p) => ({ ...p, borderColor: c, borderWidth: p.borderWidth > 0 ? p.borderWidth : 0.04 }))}
                                                className="w-7 h-7 rounded-full"
                                                style={{ background: c, border: on ? "2px solid #fff" : "1px solid rgba(255,255,255,0.25)", boxShadow: on ? "0 0 0 2px #7F00FF" : "none" }} />
                                        );
                                    })}
                                </div>
                                {photo.borderWidth > 0 && (
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-client-font/55 text-[10px] uppercase tracking-wider">Thickness</span>
                                            <span className="text-client-font/70 text-[10px] tabular-nums">{Math.round(photo.borderWidth * 100)}</span>
                                        </div>
                                        <input type="range" min="1" max="15" value={Math.round(photo.borderWidth * 100)}
                                            onChange={(e) => setPhoto((p) => ({ ...p, borderWidth: Number(e.target.value) / 100 }))}
                                            className="w-full" style={{ accentColor: "#7F00FF" }} />
                                    </div>
                                )}
                            </div>

                            {/* Width + Height + Rotation sliders */}
                            <div className="grid grid-cols-2 gap-3.5">
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-client-font/55 text-[10px] uppercase tracking-wider flex items-center gap-1"><FiMaximize2 className="text-[11px]" /> Width</span>
                                        <span className="text-client-font/70 text-[10px] tabular-nums">{Math.round(photo.w * 100)}</span>
                                    </div>
                                    <input type="range" min="6" max="100" value={Math.round(photo.w * 100)}
                                        onChange={(e) => setPhoto((p) => ({ ...p, w: Number(e.target.value) / 100 }))}
                                        className="w-full" style={{ accentColor: "#7F00FF" }} />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-client-font/55 text-[10px] uppercase tracking-wider flex items-center gap-1"><FiMaximize2 className="text-[11px] rotate-90" /> Height</span>
                                        <span className="text-client-font/70 text-[10px] tabular-nums">{Math.round(photo.h * 100)}</span>
                                    </div>
                                    <input type="range" min="4" max="100" value={Math.round(photo.h * 100)}
                                        onChange={(e) => setPhoto((p) => ({ ...p, h: Number(e.target.value) / 100 }))}
                                        className="w-full" style={{ accentColor: "#7F00FF" }} />
                                </div>
                                <div className="col-span-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-client-font/55 text-[10px] uppercase tracking-wider flex items-center gap-1"><FiRotateCw className="text-[11px]" /> Rotate</span>
                                        <span className="text-client-font/70 text-[10px] tabular-nums">{photo.rotation || 0}°</span>
                                    </div>
                                    <input type="range" min="-180" max="180" value={photo.rotation || 0}
                                        onChange={(e) => setPhoto((p) => ({ ...p, rotation: Number(e.target.value) }))}
                                        className="w-full" style={{ accentColor: "#7F00FF" }} />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 flex-wrap pt-0.5">
                                <button onClick={toggleOrder}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-client-font/80"
                                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                    <FiLayers /> {photo.order === "front" ? "Above frame" : "Behind frame"}
                                </button>
                                <button onClick={resetPhoto}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-client-font/80"
                                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                    <FiRefreshCw /> Reset
                                </button>
                                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer text-client-font/80"
                                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                    <FiUpload /> Change
                                    <input type="file" accept={PHOTO_TYPES.join(",")} className="hidden" onChange={onPickPhoto} />
                                </label>
                                <button onClick={removePhoto}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-red-300 ml-auto"
                                    style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}>
                                    <FiTrash2 /> Remove
                                </button>
                            </div>

                            <p className="text-client-font/45 text-[11px] text-center">Drag the photo to move • drag the purple corner to resize</p>
                        </div>
                    ))}
                </div>

                {/* ---- Text ---- */}
                <div className="mt-5">
                    <div className="flex items-center justify-between mb-2">
                        <button onClick={() => setShowText((s) => !s)} className="flex items-center gap-1.5">
                            <span className="text-client-font/70 text-xs font-semibold uppercase tracking-wider">Text</span>
                            <FiChevronDown className="text-client-font/55 transition-transform" style={{ transform: showText ? "rotate(180deg)" : "none" }} />
                        </button>
                        <button onClick={() => { setShowText(true); addText(); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white"
                            style={{ background: "linear-gradient(135deg,#7F00FF,#FF8AFF)" }}>
                            <FiPlus /> Add text
                        </button>
                    </div>

                    {showText && (texts.length === 0 ? (
                        <p className="text-client-font/45 text-[11px]">Add text (your name, message…) and place it anywhere on the design.</p>
                    ) : (
                        <div className="rounded-2xl p-3.5 space-y-3.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            {/* layer selector */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {texts.map((t) => (
                                    <button key={t.id} onClick={() => setSelTextId(t.id)}
                                        className="inline-flex items-center gap-1.5 max-w-[150px] px-2.5 py-1.5 rounded-lg text-xs"
                                        style={selTextId === t.id
                                            ? { color: "#fff", background: "linear-gradient(135deg,#7F00FF,#FF8AFF)" }
                                            : { color: "rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                        <FiType className="text-[11px] flex-shrink-0" />
                                        <span className="truncate">{t.content || "Text"}</span>
                                    </button>
                                ))}
                            </div>

                            {selText && (
                                <>
                                    <input value={selText.content} onChange={(e) => updateSel({ content: e.target.value })} placeholder="Type your text…"
                                        className="w-full px-3 py-2 rounded-lg text-sm text-client-font outline-none focus:ring-2 focus:ring-main-blue"
                                        style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }} />

                                    {/* font */}
                                    <div>
                                        <p className="text-client-font/55 text-[10px] uppercase tracking-wider mb-1.5">Font</p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {FONTS.map((f) => (
                                                <button key={f.label} onClick={() => updateSel({ font: f.css })}
                                                    className="px-3 py-1.5 rounded-lg text-xs"
                                                    style={{ fontFamily: f.css, ...(selText.font === f.css
                                                        ? { color: "#fff", background: "linear-gradient(135deg,#7F00FF,#FF8AFF)" }
                                                        : { color: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }) }}>
                                                    {f.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* color */}
                                    <div>
                                        <p className="text-client-font/55 text-[10px] uppercase tracking-wider mb-1.5">Color</p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {COLORS.map((c) => (
                                                <button key={c} onClick={() => updateSel({ color: c })}
                                                    className="w-7 h-7 rounded-full"
                                                    style={{ background: c, border: selText.color === c ? "2px solid #fff" : "1px solid rgba(255,255,255,0.25)", boxShadow: selText.color === c ? "0 0 0 2px #7F00FF" : "none" }} />
                                            ))}
                                            <label className="w-7 h-7 rounded-full overflow-hidden relative cursor-pointer" style={{ border: "1px solid rgba(255,255,255,0.25)" }} title="Custom color">
                                                <span className="absolute inset-0" style={{ background: "conic-gradient(red,yellow,lime,cyan,blue,magenta,red)" }} />
                                                <input type="color" value={selText.color} onChange={(e) => updateSel({ color: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" />
                                            </label>
                                        </div>
                                    </div>

                                    {/* size + rotate */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-client-font/55 text-[10px] uppercase tracking-wider flex items-center gap-1"><FiMaximize2 className="text-[11px]" /> Size</span>
                                                <span className="text-client-font/70 text-[10px] tabular-nums">{Math.round(selText.size * 100)}</span>
                                            </div>
                                            <input type="range" min="2" max="40" value={Math.round(selText.size * 100)}
                                                onChange={(e) => updateSel({ size: Number(e.target.value) / 100 })} className="w-full" style={{ accentColor: "#7F00FF" }} />
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-client-font/55 text-[10px] uppercase tracking-wider flex items-center gap-1"><FiRotateCw className="text-[11px]" /> Rotate</span>
                                                <span className="text-client-font/70 text-[10px] tabular-nums">{selText.rotation || 0}°</span>
                                            </div>
                                            <input type="range" min="-180" max="180" value={selText.rotation || 0}
                                                onChange={(e) => updateSel({ rotation: Number(e.target.value) })} className="w-full" style={{ accentColor: "#7F00FF" }} />
                                        </div>
                                    </div>

                                    {/* style effects */}
                                    <div>
                                        <p className="text-client-font/55 text-[10px] uppercase tracking-wider mb-1.5">Style</p>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {[
                                                { key: "bold", label: "Bold", icon: <FiBold /> },
                                                { key: "italic", label: "Italic", icon: <FiItalic /> },
                                                { key: "stroke", label: "Outline", icon: <span className="text-[11px] font-bold" style={{ WebkitTextStroke: "0.8px currentColor", color: "transparent" }}>O</span> },
                                                { key: "bg", label: "Highlight", icon: <span className="px-1 rounded text-[9px] font-bold" style={{ background: "currentColor" }}><span style={{ color: "#1a1a1a" }}>A</span></span> },
                                            ].map((s) => (
                                                <button key={s.key} onClick={() => updateSel({ [s.key]: !selText[s.key] })}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                                                    style={selText[s.key]
                                                        ? { color: "#fff", background: "linear-gradient(135deg,#7F00FF,#FF8AFF)" }
                                                        : { color: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }}>
                                                    {s.icon} {s.label}
                                                </button>
                                            ))}
                                            <button onClick={() => removeText(selText.id)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-red-300 ml-auto"
                                                style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}>
                                                <FiTrash2 /> Delete
                                            </button>
                                        </div>
                                    </div>

                                    <p className="text-client-font/45 text-[11px] text-center">Drag the text to move • drag the purple corner to resize</p>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {/* ---- Frame picker ---- */}
                {connected.length > 0 && (
                    <div className="mt-5">
                        <p className="text-client-font/70 text-xs font-semibold uppercase tracking-wider mb-2">Choose a frame</p>
                        <div className="flex items-stretch gap-2.5 overflow-x-auto no-scrollbar pb-1">
                            <button onClick={() => setOverlayId(null)}
                                className="flex-shrink-0 w-14 rounded-xl border-2 flex flex-col items-center justify-center gap-1 transition"
                                style={{ aspectRatio: "9 / 16", borderColor: !overlayId ? "#B14BFF" : "rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", boxShadow: !overlayId ? "0 0 0 3px rgba(127,0,255,0.25)" : "none" }}>
                                <FiCheck className={!overlayId ? "text-client-title" : "text-client-font/40"} />
                                <span className={`text-[9px] font-medium ${!overlayId ? "text-client-title" : "text-client-font/50"}`}>None</span>
                            </button>
                            {connected.map((f) => (
                                <button key={f._id} onClick={() => setOverlayId(f._id)} title={f.f_name}
                                    className="flex-shrink-0 w-14 rounded-xl border-2 overflow-hidden transition"
                                    style={{ aspectRatio: "9 / 16", borderColor: overlayId === f._id ? "#B14BFF" : "rgba(255,255,255,0.14)", boxShadow: overlayId === f._id ? "0 0 0 3px rgba(127,0,255,0.25)" : "none", ...CHECKER }}>
                                    <img src={f.f_thumbnail || f.f_image} alt={f.f_name} loading="lazy" className="w-full h-full object-contain" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* ---- Download ---- */}
                <button onClick={handleDownload} disabled={busy}
                    className="w-full mt-5 py-3.5 rounded-full text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: "linear-gradient(135deg, #7F00FF, #FF8AFF)", boxShadow: "0 14px 30px -10px rgba(127,0,255,0.7)" }}>
                    <FiDownload className="text-base" />
                    {busy ? "Preparing…" : overlay ? `Download · ${overlay.f_name}` : "Download"}
                </button>

                {/* ---- Recommended (same category) ---- */}
                {related.length > 0 && (
                    <div className="mt-10">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1 h-5 rounded-full" style={{ background: "linear-gradient(180deg, #7F00FF, #FCFA02)" }} />
                            <h2 className="text-client-font font-semibold text-sm md:text-base tracking-wide uppercase">
                                More in {data.i_category?.i_name || "this category"}
                            </h2>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                            {related.map((item) => (
                                <RelatedCard key={item._id} item={item} onOpen={openTemplate} />
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <InpixFooter />

            {/* ---- Processing dialog — portal to body so it's truly viewport-centered ---- */}
            {busy && createPortal(
                <div className="fixed inset-0 flex items-center justify-center px-6"
                    style={{ zIndex: 99999, background: "rgba(10,8,22,0.72)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
                    <div className="flex flex-col items-center gap-4 px-8 py-8 rounded-3xl text-center max-w-xs w-full"
                        style={{
                            background: "rgba(255,255,255,0.08)",
                            backdropFilter: "blur(22px)", WebkitBackdropFilter: "blur(22px)",
                            border: "1px solid rgba(255,255,255,0.16)",
                            boxShadow: "0 24px 70px -20px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                        }}>
                        <div className="w-14 h-14 rounded-full animate-spin"
                            style={{ border: "4px solid rgba(255,255,255,0.18)", borderTopColor: "#FF8AFF" }} />
                        <div>
                            <p className="text-white font-semibold text-base">Processing your status…</p>
                            <p className="text-white/75 text-xs mt-1.5 leading-relaxed">
                                {isVideo
                                    ? "Merging your video — this runs in real-time, so it takes about as long as the clip. Please don't close this page."
                                    : "Preparing your download…"}
                            </p>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ClientInpixView;
